import React, { ReactNode } from 'react';
import mustache from 'mustache';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../../hooks/Analytics';
import CheckMarkIcon from '../../../../ui/icons/CheckMark/CheckMark';
import { useTranslations } from '../../../../../hooks/useTranslations';
import { LayoutTree } from '../../../DynamicLayout';
import { NotificationPreferences } from '../../../../../../../__generated__/loma';
import { Channels, PreferenceLevels } from '../../types';
import LineLogo from '../common/LineLogo';
import ControlView from '../ControlView/ControlView';
import { useGlobalSettings } from '../../../../../hooks/useGlobalSettings';
import { useIsTrackFeatureOn } from '../../../../../hooks/useGrowthbook';

const { FeatureLayout } = LayoutTree;

export interface SubscribedProps {
  onUpdateContactInfo: () => void;
  onUnsubscribe: () => void;
  notificationPreferencesState?: NotificationPreferences;
  channel: Channels;
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
}

export interface SubscribedChannelProps {
  updateContactInfoIcon: ReactNode;
  updateContactInfoMessage: string;
  updateContextInfoText?: string;
  updateContactInfoButtonText?: string;
  unsubscribeLink?: string;
  eventComponent: string;
  eventCategory?: string;
  unsubscribeEvent?: string;
}

const Subscribed = ({
  onUpdateContactInfo,
  onUnsubscribe,
  featureName,
  layout,
  channel,
  notificationPreferencesState,
}: SubscribedProps) => {
  const { orderLevelOptInEnabled, retailerLevelOptInEnabled, retailerName } =
    useGlobalSettings();

  const { preferenceLevel } = notificationPreferencesState || {};
  const retailerLevel = preferenceLevel === PreferenceLevels.RETAILER;

  const {
    notificationCenterSmsUpdateNumberMessage,
    notificationCenterSmsUpdateNumberButton,
    notificationCenterSmsOptoutLink,
    notificationCenterAppPushUpdateMessage,
    notificationCenterEmailUpdateMessage,
    notificationCenterEmailUpdateButton,
    notificationCenterEmailOptoutLink,
    notificationCenterLineUpdateMessage,
    notificationCenterLineOptoutLink,
    notificationCenterLineOptoutLinkRetailerLevel,
    notificationCenterLineUpdateMessageRetailerLevel,
    notificationCenterSmsUpdateNumberMessageRetailerLevel,
    notificationCenterSmsOptoutLinkRetailerLevel,
    notificationCenterEmailUpdateMessageRetailerLevel,
    notificationCenterEmailOptoutLinkRetailerLevel,
    notificationCenterSublimeManageYourPreferences,
    notificationCenterSublimeAutoOptinTitle,
    notificationCenterSublimeAutoOptinText,
    notificationCenterSublimeUnsubscribeFromAll,
  } = useTranslations();

  const logEvent = useAnalyticsPost();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  const smsSubscribedChannelProps: SubscribedChannelProps = {
    updateContactInfoIcon: <CheckMarkIcon />,
    updateContactInfoMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterSmsUpdateNumberMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterSmsUpdateNumberMessage) || '',
    updateContactInfoButtonText: notificationCenterSmsUpdateNumberButton || '',
    unsubscribeLink:
      (retailerLevel
        ? mustache.render(notificationCenterSmsOptoutLinkRetailerLevel || '', {
            retailerName,
          })
        : notificationCenterSmsOptoutLink) || '',
    eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
    eventCategory: NarvarClickSubTypes.SMS_NUMBER_UPDATE,
    unsubscribeEvent: NarvarClickSubTypes.SMS_UNSUBSCRIBE,
  };

  const appPushSubscribedChannelProps: SubscribedChannelProps = {
    updateContactInfoIcon: <CheckMarkIcon />,
    updateContactInfoMessage: notificationCenterAppPushUpdateMessage || '',
    eventComponent: NarvarEventComponents.NTH_PACK_APP_PUSH_NOTIFICATION_CENTER,
  };

  const emailSubscribedChannelProps: SubscribedChannelProps = {
    updateContactInfoIcon: <CheckMarkIcon />,
    updateContactInfoMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterEmailUpdateMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterEmailUpdateMessage) || '',
    updateContactInfoButtonText: notificationCenterEmailUpdateButton || '',
    unsubscribeLink:
      (retailerLevel
        ? mustache.render(
            notificationCenterEmailOptoutLinkRetailerLevel || '',
            {
              retailerName,
            },
          )
        : notificationCenterEmailOptoutLink) || '',
    eventComponent: NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
    eventCategory: NarvarClickSubTypes.EMAIL_UPDATE,
    unsubscribeEvent: NarvarClickSubTypes.EMAIL_UNSUBSCRIBE,
  };

  const lineSubscribedChannelProps: SubscribedChannelProps = {
    updateContactInfoIcon: <LineLogo />,
    updateContactInfoMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterLineUpdateMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterLineUpdateMessage) || '',
    updateContactInfoButtonText: '',
    unsubscribeLink:
      (retailerLevel
        ? mustache.render(notificationCenterLineOptoutLinkRetailerLevel || '', {
            retailerName,
          })
        : notificationCenterLineOptoutLink) || '',
    eventComponent: NarvarEventComponents.NTH_PACK_LINE_NOTIFICATION_CENTER,
    unsubscribeEvent: NarvarClickSubTypes.LINE_UNSUBSCRIBE,
  };

  const sublimeSubscribedSubscribedChannelProps: SubscribedChannelProps = {
    updateContactInfoIcon: <CheckMarkIcon />,
    updateContactInfoMessage: notificationCenterSublimeAutoOptinTitle ?? '',
    updateContextInfoText: notificationCenterSublimeAutoOptinText ?? '',
    updateContactInfoButtonText:
      notificationCenterSublimeManageYourPreferences ?? '',
    unsubscribeLink: notificationCenterSublimeUnsubscribeFromAll ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_LINE_NOTIFICATION_CENTER,
    unsubscribeEvent: NarvarClickSubTypes.LINE_UNSUBSCRIBE,
  };

  const subscribedChannelProps: {
    [key in Channels]: SubscribedChannelProps;
  } = {
    [Channels.SMS]: smsSubscribedChannelProps,
    [Channels.HIPAA_SMS]: smsSubscribedChannelProps,
    [Channels.EMAIL]: emailSubscribedChannelProps,
    [Channels.HIPAA_EMAIL]: emailSubscribedChannelProps,
    [Channels.APP_PUSH]: appPushSubscribedChannelProps,
    [Channels.LINE]: lineSubscribedChannelProps,
    [Channels.SUBLIME]: sublimeSubscribedSubscribedChannelProps,
  };

  const {
    updateContactInfoIcon,
    updateContactInfoMessage,
    updateContextInfoText,
    updateContactInfoButtonText,
    unsubscribeLink,
    eventComponent,
    eventCategory,
    unsubscribeEvent,
  }: SubscribedChannelProps = subscribedChannelProps[channel];

  const handlePrimaryButtonClick = () => {
    logEvent({
      clickCategory: eventCategory,
      clickNotificationIsChangeNumber: true,
      clickSubType: eventCategory,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent,
      eventFeature: eventComponent,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: eventComponent,
    });
    onUpdateContactInfo();
  };

  const handleSecondaryButtonClick = () => {
    onUnsubscribe();
    logEvent({
      clickCategory: unsubscribeEvent,
      clickNotificationIsOptOut: true,
      clickSubType: unsubscribeEvent,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent,
      eventFeature: eventComponent,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: eventComponent,
    });
  };

  const { enabled: notificationPreferencesUnsubscribeEnabled } =
    useIsTrackFeatureOn('notification-preferences-unsubscribe');

  const retailerPreference = preferenceLevel === PreferenceLevels.RETAILER;
  const orderPreference = preferenceLevel === PreferenceLevels.ORDER;

  const getShowSubscribeActions = () => {
    const subscribeActions = {
      showUnsubscribeAction: true,
      showUpdateAction: true,
    };

    if (
      (retailerPreference && !retailerLevelOptInEnabled) ||
      (orderPreference && !orderLevelOptInEnabled) ||
      channel === Channels.APP_PUSH
    ) {
      subscribeActions.showUnsubscribeAction = false;
      subscribeActions.showUpdateAction = false;
    }

    if (channel === Channels.LINE) {
      subscribeActions.showUpdateAction = false;
    }

    if (
      channel === Channels.SUBLIME &&
      !notificationPreferencesUnsubscribeEnabled
    ) {
      subscribeActions.showUnsubscribeAction = false;
    }

    return subscribeActions;
  };

  const { showUpdateAction, showUnsubscribeAction } = getShowSubscribeActions();

  return (
    <ControlView
      featureName={featureName}
      isLandscape={isLandscape}
      channel={channel}
      icon={updateContactInfoIcon}
      heading={updateContactInfoMessage}
      text={updateContextInfoText}
      primaryButton={
        showUpdateAction
          ? {
              onClick: handlePrimaryButtonClick,
              text: updateContactInfoButtonText ?? '',
            }
          : undefined
      }
      secondaryButton={
        showUnsubscribeAction
          ? {
              onClick: handleSecondaryButtonClick,
              text: unsubscribeLink ?? '',
            }
          : undefined
      }
    />
  );
};

export default Subscribed;
